import { ComponentType, ReactNode, useState } from "react";

import CalculateIcon from "@mui/icons-material/CalculateOutlined";
import FamilyIcon from "@mui/icons-material/FamilyRestroomOutlined";
import TargetIcon from "@mui/icons-material/TrackChangesOutlined";
import { Box, Stack, Typography } from "@mui/material";

import { AppointmentTargetCalculatorDialog } from "@/common/components/calculators/appointment-target-calculator-dialog/AppointmentTargetCalculatorDialog";
import { SeizableIncomeCalculatorDialog } from "@/common/components/calculators/seizable-income-calculator-dialog/SeizableIncomeCalculatorDialog";
import { SpousalMaintenanceCalculatorDialog } from "@/common/components/calculators/spousal-maintenance-calculator-dialog/SpousalMaintenanceCalculatorDialog";
import { useTranslation } from "@/translations";

import { CalculatorCard } from "./components/CalculatorCard";

// Hard-coded for now — the list grows with each new calculator (further ones are planned). Each entry
// carries its own overlay, so a card can never be added without the dialog it opens.
type CalculatorKey = "seizableIncome" | "spousalMaintenance" | "appointmentTargets";

interface CalculatorEntry {
  key: CalculatorKey;
  icon: ReactNode;
  Dialog: ComponentType<{ onClose: () => void }>;
}

export function Calculators() {
  const { t } = useTranslation();
  const [openCalculator, setOpenCalculator] = useState<CalculatorKey | null>(null);

  const cards: CalculatorEntry[] = [
    {
      key: "seizableIncome",
      icon: <CalculateIcon color="primary" fontSize="large" />,
      Dialog: SeizableIncomeCalculatorDialog,
    },
    {
      key: "spousalMaintenance",
      icon: <FamilyIcon color="primary" fontSize="large" />,
      Dialog: SpousalMaintenanceCalculatorDialog,
    },
    {
      key: "appointmentTargets",
      icon: <TargetIcon color="primary" fontSize="large" />,
      Dialog: AppointmentTargetCalculatorDialog,
    },
  ];

  const OpenDialog = cards.find((card) => card.key === openCalculator)?.Dialog;

  return (
    <Stack spacing={3} sx={{ height: "100%" }} pb={3}>
      <Typography variant="h1">{t("calculators.title")}</Typography>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          maxWidth: 1200,
        }}
      >
        {cards.map(({ key, icon }) => (
          <CalculatorCard
            key={key}
            icon={icon}
            title={t(`calculators.${key}.title`)}
            subtitle={t(`calculators.${key}.subtitle`)}
            description={t(`calculators.${key}.description`)}
            onOpen={() => setOpenCalculator(key)}
          />
        ))}
      </Box>

      {/* Unmounting on close is what discards the inputs — nothing about a calculation is persisted. */}
      {OpenDialog && <OpenDialog onClose={() => setOpenCalculator(null)} />}
    </Stack>
  );
}
